/**
 * Turning what somebody pasted into a video the page can embed.
 *
 * Nothing here ever puts a pasted address into the page. The identifier is
 * pulled out of it and an embed address is composed from scratch, so the only
 * thing that can reach the iframe is eleven characters of YouTube identifier.
 * That is what stops a `javascript:` or `data:` address -- or an embed of some
 * other site entirely -- arriving through the administration form.
 *
 * The Worker validates with the same function before storing anything, so a
 * request made outside the admin page is refused rather than stored and then
 * quietly ignored by the front page. See routes/admin.ts.
 */

/** A YouTube video identifier: eleven characters of URL-safe base64. */
const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;

/** The hosts a YouTube address is written with, including the shortener. */
const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'youtu.be',
  'www.youtu.be',
]);

/**
 * The path forms that carry the identifier as the last segment: an embed, a
 * short, and the permalink a live stream leaves behind.
 */
const PATH_PREFIXES = ['/embed/', '/shorts/', '/live/', '/v/'];

/**
 * The video identifier in a YouTube address, or an empty string if there is not
 * one. Accepts a bare identifier too, since that is what somebody who has done
 * this before tends to paste.
 *
 * Deliberately forgiving about the form of the address -- a share link carries a
 * tracking parameter, a link copied at a timestamp carries `t`, and a phone
 * gives the `m.` host -- because the alternative is an administrator being told
 * their perfectly good link is wrong.
 */
export function youtubeVideoId(value: string): string {
  // Not merely defensive. /api/config is a response from another program, and a
  // memorial deployed before this field existed has a stored configuration with
  // no youtubeUrl in it -- so this is reached with undefined on the first load
  // after an upgrade, whatever the type says. Throwing there would take the
  // whole home page down with it.
  if (typeof value !== 'string') return '';

  const trimmed = value.trim();
  if (trimmed === '') return '';
  if (VIDEO_ID.test(trimmed)) return trimmed;

  // A pasted address often arrives without its scheme. Assuming https rather
  // than rejecting it is safe because the host is checked below regardless.
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return '';
  }

  // http: and https: only. Without this, "javascript://youtube.com/watch?v=..."
  // parses happily and hands back an identifier.
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
  if (!YOUTUBE_HOSTS.has(url.hostname.toLowerCase())) return '';

  const candidate = fromWatch(url) ?? fromPath(url) ?? fromShortener(url);
  return candidate !== null && VIDEO_ID.test(candidate) ? candidate : '';
}

/** youtube.com/watch?v=ID */
function fromWatch(url: URL): string | null {
  return url.pathname === '/watch' ? url.searchParams.get('v') : null;
}

/** youtube.com/embed/ID, /shorts/ID, /live/ID, /v/ID */
function fromPath(url: URL): string | null {
  const prefix = PATH_PREFIXES.find((candidate) => url.pathname.startsWith(candidate));
  return prefix === undefined ? null : url.pathname.slice(prefix.length).split('/')[0] ?? null;
}

/** youtu.be/ID */
function fromShortener(url: URL): string | null {
  const host = url.hostname.toLowerCase();
  if (host !== 'youtu.be' && host !== 'www.youtu.be') return null;
  return url.pathname.slice(1).split('/')[0] ?? null;
}

/** Whether a value can be shown as a video. An empty string can: it means none. */
export function isEmbeddableYouTubeUrl(value: string): boolean {
  if (typeof value !== 'string') return true;
  return value.trim() === '' || youtubeVideoId(value) !== '';
}

/**
 * The address to embed, composed here rather than taken from what was pasted.
 *
 * `youtube-nocookie.com` is the privacy-preserving host: YouTube does not write
 * its usual tracking cookies until somebody actually presses play. For a
 * memorial that people arrive at from a funeral notice, not tracking a visitor
 * who only came to read is worth the slightly longer host name.
 *
 * `mute=1` travels with `autoplay=1` and is not optional. Every current browser
 * refuses to start a video with sound before the visitor has interacted with
 * the page, so without it the video would silently not start at all. It is also
 * the kinder default here: sound beginning on its own is the last thing someone
 * opening a memorial in a quiet room or an office wants.
 *
 * `rel=0` is what keeps the suggestions at the end on the same channel. It no
 * longer removes them -- YouTube changed that in 2018 -- so this is as far as an
 * embed can go, and it is what stops the video finishing on somebody else's
 * unrelated content.
 *
 * `playsinline=1` keeps the video in the page on an iPhone instead of throwing
 * it into the native fullscreen player.
 */
export function youtubeEmbedUrl(value: string): string {
  const id = youtubeVideoId(value);
  if (id === '') return '';

  const parameters = new URLSearchParams({
    autoplay: '1',
    mute: '1',
    rel: '0',
    playsinline: '1',
  });

  return `https://www.youtube-nocookie.com/embed/${id}?${parameters.toString()}`;
}
